
import { useState, useEffect } from 'react'
import type { Canvas } from '@/hooks/useCanvas'
import type { CanvasConfig } from '@/types/canvas'

interface UseCanvasSidebarConfigParams {
  currentCanvas: Canvas | null
  activePage: string | null
  setActivePage: (pageId: string) => void
  pages: Array<{ id: string; uuid: string; title: string; order: number }>
  saveConfig: (config: CanvasConfig) => void
}

interface UseCanvasSidebarConfigReturn {
  sidebarCollapsed: boolean
  setSidebarCollapsed: React.Dispatch<React.SetStateAction<boolean>>
}

/**
 * Manages sidebar collapsed state and active page persistence to canvas config.
 *
 * Responsibilities:
 * - Load sidebar collapsed state from canvas config on mount
 * - Load active page from canvas config on mount
 * - Persist sidebar collapsed + active page changes to canvas config
 */
export function useCanvasSidebarConfig({
  currentCanvas,
  activePage,
  setActivePage,
  pages,
  saveConfig,
}: UseCanvasSidebarConfigParams): UseCanvasSidebarConfigReturn {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true)
  const [sidebarCollapsedInitialized, setSidebarCollapsedInitialized] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)

  // Load sidebar state from canvas config (independent of pages)
  useEffect(() => {
    if (currentCanvas && !sidebarCollapsedInitialized) {
      const config = currentCanvas.config as CanvasConfig | undefined
      if (config?.ui?.sidebarCollapsed !== undefined) {
        setSidebarCollapsed(config.ui.sidebarCollapsed)
      }
      setSidebarCollapsedInitialized(true)
    }
  }, [currentCanvas, sidebarCollapsedInitialized])

  // Load active page from canvas config (AFTER pages are loaded)
  useEffect(() => {
    if (currentCanvas && sidebarCollapsedInitialized && pages.length > 0 && isInitialLoad) {
      const config = currentCanvas.config as CanvasConfig | undefined
      const isDefaultPage = activePage === pages[0]?.id
      const activePageId = config?.ui?.activePageId
      if (activePageId && isDefaultPage) {
        const pageExists = pages.find(p => p.id === activePageId)
        if (pageExists) {
          setActivePage(activePageId)
        }
      }
      setIsInitialLoad(false)
    }
  }, [currentCanvas, sidebarCollapsedInitialized, pages, activePage, setActivePage, isInitialLoad])

  // Save sidebar state and active page to canvas config when they change (skip initial load)
  useEffect(() => {
    if (sidebarCollapsedInitialized && currentCanvas && !isInitialLoad) {
      const config = (currentCanvas.config as CanvasConfig | undefined) || {}
      const newConfig: CanvasConfig = {
        ...config,
        ui: {
          ...(config.ui || {}),
          sidebarCollapsed,
          activePageId: activePage
        }
      }
      saveConfig(newConfig)
    }
  }, [sidebarCollapsed, activePage, isInitialLoad, sidebarCollapsedInitialized, currentCanvas, saveConfig])

  return {
    sidebarCollapsed,
    setSidebarCollapsed,
  }
}
